import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

import javascript from "../assets/skills/javascript.svg";
import typescript from "../assets/skills/typescript.svg";
import linux from "../assets/skills/linux.svg";
import python from "../assets/skills/python.svg";
import windows from "../assets/skills/windows.svg";
import docker from "../assets/skills/docker.svg";
import git from "../assets/skills/git.svg";
import express from "../assets/skills/express.svg";
import nodejs from "../assets/skills/nodejs.svg";
import nextjs from "../assets/skills/nextjs.svg";
import nestjs from "../assets/skills/nestjs.svg";
import graphql from "../assets/skills/graphql.svg";
import aws from "../assets/skills/aws.svg";
import kubernetes from "../assets/skills/kubernetes.svg";
import tailwind from "../assets/skills/tailwind.svg";
import reactIcon from "../assets/skills/react.svg";
import golang from "../assets/skills/golang.svg";

const skillsData = {
  name: "Skills",
  children: [
    {
      name: "Frontend",
      color: "#818cf8",
      children: [
        { name: "React", img: reactIcon, value: 5 },
        { name: "Next.js", img: nextjs, value: 4 },
        { name: "Tailwind", img: tailwind, value: 4 },
        { name: "JavaScript", img: javascript, value: 5 },
        { name: "TypeScript", img: typescript, value: 5 },
      ],
    },
    {
      name: "Backend",
      color: "#22d3ee",
      children: [
        { name: "Node.js", img: nodejs, value: 5 },
        { name: "NestJS", img: nestjs, value: 4 },
        { name: "Express", img: express, value: 4 },
        { name: "GraphQL", img: graphql, value: 3 },
        { name: "Python", img: python, value: 3 },
        { name: "Go", img: golang, value: 3 },
      ],
    },
    {
      name: "DevOps", 
      color: "#fbbf24",
      children: [
        { name: "AWS", img: aws, value: 4 },
        { name: "Docker", img: docker, value: 4 },
        { name: "K8s", img: kubernetes, value: 3 },
        { name: "Linux", img: linux, value: 4 },
        { name: "Git", img: git, value: 5 },
        { name: "Windows", img: windows, value: 3 },
      ],
    },
  ],
};

export default function SkillConstellation() {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = '';
    
    const rect = container.getBoundingClientRect();
    const width = rect.width || 700;
    const height = 700;

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    // Build a flat nodes array + links
    const nodes = [];
    const links = [];

    // Center node
    nodes.push({ id: 'center', name: '', fx: width / 2, fy: height / 2, r: 0, type: 'center' });

    skillsData.children.forEach((category) => {
      const catNode = { id: category.name, name: category.name, r: 38, type: 'category', color: category.color };
      nodes.push(catNode);
      links.push({ source: 'center', target: category.name, distance: 180 });

      category.children.forEach((skill) => {
        const skillNode = { id: skill.name, name: skill.name, img: skill.img, r: 30, type: 'skill', color: category.color, value: skill.value };
        nodes.push(skillNode);
        links.push({ source: category.name, target: skill.name, distance: 110 });
      });
    });

    const simulation = d3
      .forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d) => d.id).distance((d) => d.distance))
      .force('charge', d3.forceManyBody().strength(-350))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d) => d.r + 12))
      .force('boundX', () => {
        nodes.forEach((d) => {
          if (d.type === 'center') return;
          const pad = d.r + 40;
          d.x = Math.max(pad, Math.min(width - pad, d.x));
          d.y = Math.max(pad, Math.min(height - pad, d.y));
        });
      });

    // Draw links
    const link = svg
      .append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#818cf8')
      .attr('stroke-opacity', 0.2)
      .attr('stroke-width', 1.5);

    // Draw nodes group
    const node = svg
      .append('g')
      .selectAll('g')
      .data(nodes.filter((d) => d.type !== 'center'))
      .enter()
      .append('g')
      .style('cursor', 'grab')
      .call(d3.drag().on('start', dragStarted).on('drag', dragged).on('end', dragEnded));

    // Glow circles for categories
    node
      .filter((d) => d.type === 'category')
      .append('circle')
      .attr('r', (d) => d.r + 6)
      .attr('fill', 'none')
      .attr('stroke', (d) => d.color)
      .attr('stroke-opacity', 0.2)
      .attr('stroke-width', 3);

    // Main circles
    node
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d) => d.type === 'category' ? d.color + '20' : '#1e2740')
      .attr('stroke', (d) => d.color)
      .attr('stroke-width', (d) => d.type === 'category' ? 2.5 : 1.5)
      .attr('stroke-opacity', (d) => d.type === 'category' ? 0.8 : 0.3);

    // Skill icons
    node
      .filter((d) => d.type === 'skill')
      .append('image')
      .attr('href', (d) => d.img)
      .attr('width', 28)
      .attr('height', 28)
      .attr('x', -14)
      .attr('y', -14)
      .attr('opacity', 0.85);

    // Labels
    node
      .append('text')
      .text((d) => d.name)
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => d.type === 'category' ? 6 : 38)
      .attr('fill', (d) => d.type === 'category' ? d.color : '#94a3b8')
      .attr('font-size', (d) => d.type === 'category' ? '14px' : '12px')
      .attr('font-weight', (d) => d.type === 'category' ? '700' : '500')
      .attr('letter-spacing', (d) => d.type === 'category' ? '1.5px' : '0');

    // Hover effects
    node
      .on('mouseenter', function (event, d) {
        d3.select(this).select('circle').transition().duration(200).attr('r', d.r + 6).attr('stroke-opacity', 1);
        d3.select(this).select('text').transition().duration(200).attr('fill', '#fff');
      })
      .on('mouseleave', function (event, d) {
        d3.select(this).select('circle').transition().duration(200).attr('r', d.r).attr('stroke-opacity', d.type === 'category' ? 0.8 : 0.3);
        d3.select(this).select('text').transition().duration(200).attr('fill', d.type === 'category' ? d.color : '#94a3b8');
      });

    simulation.on('tick', () => {
      link.attr('x1', (d) => d.source.x).attr('y1', (d) => d.source.y).attr('x2', (d) => d.target.x).attr('y2', (d) => d.target.y);
      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    function dragStarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragEnded(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => simulation.stop();
  }, []);

  return (
    <div id="skills" className="mt-16 py-12 text-white relative">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold">Skills</h1>
        <p className="font-light text-slate-400 mt-2">Drag nodes to explore &middot; hover for details</p>
      </div>
      <div ref={containerRef} className="w-full flex justify-center overflow-hidden" style={{ minHeight: 700 }} />
    </div>
  );
}
